import { useState, useEffect, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { useAuth } from "@/contexts/AuthContext";
import features from "@/config/features";
import {
  Car,
  ArrowLeft,
  CheckCircle2,
  XCircle,
  ChevronRight,
  RotateCcw,
  Trophy,
  Loader2,
  Home,
  CreditCard,
  User,
  LogOut,
  Menu,
  X,
  Lock,
  Bike,
  Truck,
  Bus,
  Tractor,
  BookOpen,
  RefreshCw,
  Crown,
  AlertCircle,
  Video,
  Target,
  ChevronDown,
  ChevronUp,
  CheckCircle,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const categoryIcons = {
  A: Bike,
  B: Car,
  C: Truck,
  D: Bus,
  E: Truck,
  Tr: Tractor,
  R: RefreshCw,
};

const categoryColors = {
  A: { bg: "from-orange-500 to-red-500", light: "bg-orange-100 text-orange-700" },
  B: { bg: "from-blue-500 to-indigo-600", light: "bg-blue-100 text-blue-700" },
  C: { bg: "from-green-500 to-emerald-600", light: "bg-green-100 text-green-700" },
  D: { bg: "from-purple-500 to-violet-600", light: "bg-purple-100 text-purple-700" },
  E: { bg: "from-cyan-500 to-teal-600", light: "bg-cyan-100 text-cyan-700" },
  Tr: { bg: "from-amber-500 to-yellow-600", light: "bg-amber-100 text-amber-700" },
  R: { bg: "from-slate-500 to-gray-600", light: "bg-slate-100 text-slate-700" },
};

const INDEX_TO_LETTER = ["A", "B", "C", "D", "E", "F"];
const LETTER_TO_INDEX = { a: 0, b: 1, c: 2, d: 3, e: 4, f: 5 };

// Componenta pentru afișarea unei întrebări greșite
function WrongAnswerCard({ item, index }) {
  const [expanded, setExpanded] = useState(false);
  
  // Convertim răspunsul utilizatorului în litere
  const getUserAnswerLetters = () => {
    if (item.user_answer === undefined || item.user_answer === null) return "Niciun răspuns";
    // user_answer poate fi option id (ex: "a", "b") sau index
    if (Array.isArray(item.user_answer)) {
      return item.user_answer.map(a => String(a).toUpperCase()).join(", ");
    }
    return String(item.user_answer).toUpperCase();
  };
  
  // Convertim răspunsul corect în litere
  const getCorrectAnswerLetters = () => {
    if (Array.isArray(item.correct_answer)) {
      return item.correct_answer.map(c => String(c).toUpperCase()).join(", ");
    }
    return String(item.correct_answer || "").toUpperCase();
  };

  return (
    <div className="bg-white rounded-xl shadow-sm border border-red-200 overflow-hidden">
      {/* Header întrebare */}
      <div 
        className="p-4 cursor-pointer hover:bg-red-50 transition-colors"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-start gap-3">
          <div className="w-8 h-8 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
            <span className="text-red-600 font-bold text-sm">{index + 1}</span>
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-slate-800 font-medium leading-snug">
              {item.question}
            </p>
            <div className="flex flex-wrap gap-4 mt-2 text-sm">
              <span className="text-red-600">
                Răspunsul tău: <strong>{getUserAnswerLetters()}</strong>
              </span>
              <span className="text-green-600">
                Corect: <strong>{getCorrectAnswerLetters()}</strong>
              </span>
            </div>
          </div>
          <div className="flex-shrink-0">
            {expanded ? (
              <ChevronUp className="w-5 h-5 text-slate-400" />
            ) : (
              <ChevronDown className="w-5 h-5 text-slate-400" />
            )}
          </div>
        </div>
      </div>

      {/* Detalii expandate */}
      {expanded && (
        <div className="border-t border-red-100 p-4 bg-slate-50">
          {/* Imagine dacă există */}
          {item.image_url && (
            <div className="mb-4">
              <img 
                src={item.image_url} 
                alt="Imagine întrebare" 
                className="max-w-full max-h-48 rounded-lg border mx-auto"
                onError={(e) => e.target.style.display = 'none'}
              />
            </div>
          )}

          {/* Opțiunile cu evidențiere */}
          <div className="space-y-2 mb-4">
            {item.options?.map((opt, optIdx) => {
              const optionId = opt.id || INDEX_TO_LETTER[optIdx]?.toLowerCase();
              const isCorrect = Array.isArray(item.correct_answer) 
                ? item.correct_answer.includes(optionId)
                : String(item.correct_answer).toLowerCase() === optionId;
              const isUserChoice = Array.isArray(item.user_answer)
                ? item.user_answer.includes(optionId)
                : String(item.user_answer).toLowerCase() === optionId;
              
              return (
                <div 
                  key={optIdx}
                  className={`flex items-center gap-3 p-3 rounded-lg border ${
                    isCorrect 
                      ? 'bg-green-50 border-green-300' 
                      : isUserChoice 
                        ? 'bg-red-50 border-red-300' 
                        : 'bg-white border-slate-200'
                  }`}
                >
                  <span className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm ${
                    isCorrect 
                      ? 'bg-green-500 text-white' 
                      : isUserChoice 
                        ? 'bg-red-500 text-white' 
                        : 'bg-slate-200 text-slate-600'
                  }`}>
                    {INDEX_TO_LETTER[optIdx]}
                  </span>
                  <span className={`flex-1 ${isCorrect ? 'text-green-800 font-medium' : isUserChoice ? 'text-red-800' : 'text-slate-700'}`}>
                    {opt.text}
                  </span>
                  {isCorrect && <CheckCircle className="w-5 h-5 text-green-600" />}
                  {isUserChoice && !isCorrect && <XCircle className="w-5 h-5 text-red-600" />}
                </div>
              );
            })}
          </div>

          {/* Explicație text */}
          {item.explanation && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-3">
              <p className="text-sm font-medium text-blue-800 mb-1">Explicație:</p>
              <p className="text-blue-700">{item.explanation}</p>
            </div>
          )}

          {/* Buton video explicativ */}
          {item.explanation_video_url && (
            <a 
              href={item.explanation_video_url} 
              target="_blank" 
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-lg transition-colors"
            >
              <Video className="w-4 h-4" />
              Vezi explicația video
            </a>
          )}
        </div>
      )}
    </div>
  );
}

export default function TestsPage() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  const hasStandardAccess =
    user?.role === "admin" ||
    (user?.subscription_status === "active" &&
      ["standard", "premium"].includes((user?.subscription_plan || "").toLowerCase()));

  const isSubscriptionActive =
    user?.subscription_status === "active" || user?.role === "admin";

  const [view, setView] = useState("categories");
  const [licenseCategories, setLicenseCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [quizzes, setQuizzes] = useState([]);
  const [totalQuizzes, setTotalQuizzes] = useState(47);
  const [questions, setQuestions] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [currentAnswer, setCurrentAnswer] = useState(undefined);
  const [testResult, setTestResult] = useState(null);
  const [currentQuizNumber, setCurrentQuizNumber] = useState(null);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [imageError, setImageError] = useState(false);

  useEffect(() => {
    fetchLicenseCategories();
  }, []);

  useEffect(() => {
    setImageError(false);
  }, [currentIndex]);

  // Când se schimbă întrebarea curentă, restaurăm răspunsul salvat sau resetăm
  useEffect(() => {
    if (!questions?.length) return;
    
    const existing = answers[currentIndex];
    const q = questions[currentIndex];
    const multiple = q?.multiple_answers || false;

    if (existing !== undefined) {
      setCurrentAnswer(existing);
    } else {
      setCurrentAnswer(multiple ? [] : undefined);
    }
  }, [currentIndex, questions, answers]);

  const fetchLicenseCategories = async () => {
    try {
      const response = await axios.get(`${API}/tests/license-categories`);
      setLicenseCategories(response.data);
    } catch (error) {
      console.error("Error fetching categories:", error);
    }
  };

  const selectLicenseCategory = async (category) => {
    if (!hasStandardAccess) {
      toast.error("Disponibil doar în pachetul Standard sau Premium");
      navigate("/abonamente");
      return;
    }

    setSelectedCategory(category);
    setLoading(true);

    try {
      const response = await axios.get(`${API}/tests/quizzes/${category.code}`);
      setQuizzes(response.data.quizzes || []);
      setTotalQuizzes(response.data.total_quizzes || 30);
      setView("quizzes");
    } catch (error) {
      toast.error("Eroare la încărcarea chestionarelor");
    } finally {
      setLoading(false);
    }
  };

  const goToChapterQuizzes = () => {
    if (!hasStandardAccess) {
      toast.error("Disponibil doar în pachetul Standard sau Premium");
      navigate("/abonamente");
      return;
    }

    navigate("/teste/capitole");
  };

  const startQuiz = async (quizNumber) => {
    setLoading(true);

    try {
      const response = await axios.get(
        `${API}/tests/quiz/${selectedCategory.code}/${quizNumber}`
      );
      setQuestions(response.data.questions || []);
      setCurrentQuizNumber(quizNumber);
      setAnswers({});
      setCurrentIndex(0);
      setCurrentAnswer(undefined);
      setTestResult(null);
      setView("test");
    } catch (error) {
      toast.error(
        error.response?.data?.detail || "Eroare la încărcarea chestionarului"
      );
    } finally {
      setLoading(false);
    }
  };

  const currentQuestion = questions[currentIndex];
  const isMultiple = currentQuestion?.multiple_answers || false;
  const totalQuestions = questions.length;

  // Statistici live
  const stats = useMemo(() => {
    if (!questions?.length) {
      return { resolved: 0, remaining: 0 };
    }

    let resolved = 0;
    questions.forEach((q, idx) => {
      const userAnswer = answers[idx];
      if (userAnswer !== undefined && (Array.isArray(userAnswer) ? userAnswer.length > 0 : true)) {
        resolved += 1;
      }
    });

    return {
      resolved,
      remaining: questions.length - resolved,
    };
  }, [questions, answers]);

  const handleSelect = (optionIndex) => {
    if (!currentQuestion) return;

    if (isMultiple) {
      const current = Array.isArray(currentAnswer) ? currentAnswer : [];
      const updated = current.includes(optionIndex)
        ? current.filter((i) => i !== optionIndex)
        : [...current, optionIndex].sort((a, b) => a - b);
      setCurrentAnswer(updated);
    } else {
      setCurrentAnswer(optionIndex);
    }
  };

  const hasSelection = isMultiple
    ? Array.isArray(currentAnswer) && currentAnswer.length > 0
    : currentAnswer !== undefined;

  const commitCurrentAnswer = () => {
    if (!hasSelection) return false;

    setAnswers((prev) => ({
      ...prev,
      [currentIndex]: currentAnswer,
    }));

    return true;
  };

  const prev = () => {
    if (currentIndex > 0) {
      setCurrentIndex((prevIndex) => prevIndex - 1);
    }
  };

  const next = () => {
    if (!commitCurrentAnswer()) {
      toast.error("Selectează un răspuns înainte să continui");
      return;
    }

    if (currentIndex < totalQuestions - 1) {
      setCurrentIndex((prevIndex) => prevIndex + 1);
    }
  };

  const submitTest = async () => {
    if (!commitCurrentAnswer()) {
      toast.error("Selectează un răspuns înainte să trimiți");
      return;
    }

    const finalAnswers = {
      ...answers,
      [currentIndex]: currentAnswer,
    };

    if (Object.keys(finalAnswers).length < questions.length) {
      toast.error(
        `Răspunde la toate întrebările (${Object.keys(finalAnswers).length}/${questions.length})`
      );
      return;
    }

    // Convertim răspunsurile din index în format pentru backend
    const formattedAnswers = {};
    questions.forEach((q, idx) => {
      const answer = finalAnswers[idx];
      // Găsim option id-ul bazat pe index
      if (answer !== undefined) {
        if (Array.isArray(answer)) {
          // Pentru răspunsuri multiple, trimitem array de option ids
          formattedAnswers[q.id] = answer.map(i => q.options[i]?.id).filter(Boolean);
        } else {
          formattedAnswers[q.id] = q.options[answer]?.id;
        }
      }
    });

    setSubmitting(true);

    try {
      const url = `${API}/tests/submit?quiz_number=${currentQuizNumber}&license_category=${selectedCategory.code}`;
      const response = await axios.post(url, { answers: formattedAnswers });
      setTestResult(response.data);
      setView("results");
    } catch (error) {
      console.error("Submit error:", error.response?.data || error);
      toast.error(error.response?.data?.detail || "Eroare la trimitere");
    } finally {
      setSubmitting(false);
    }
  };

  const goBackToCategories = () => {
    setView("categories");
    setSelectedCategory(null);
    setQuizzes([]);
  };

  const goBackToQuizzes = async () => {
    setView("quizzes");
    setQuestions([]);
    setCurrentQuizNumber(null);
    setAnswers({});

    // Refetch quizzes to update status (Promovat / Nepromovat)
    if (selectedCategory) {
      try {
        const response = await axios.get(`${API}/tests/quizzes/${selectedCategory.code}`);
        setQuizzes(response.data.quizzes || []);
        setTotalQuizzes(response.data.total_quizzes || 30);
      } catch (error) {
        // silent fail; keep previous list
      }
    }
  };

  const handleLogout = () => {
    logout();
    navigate("/");
    toast.success("Te-ai deconectat cu succes");
  };

  const allSidebarLinks = [
    { name: "Dashboard", path: "/dashboard", icon: Home },
    {
      name: "Cursuri Video",
      path: "/cursuri",
      icon: Video,
      requiresSubscription: true,
      feature: "videoCourses",
    },
    {
      name: "Teste DRPCIV",
      path: "/teste-drpciv",
      icon: Target,
      requiresSubscription: false,
    },
    {
      name: "Teste Antrenament",
      path: "/teste",
      icon: Target,
      requiresSubscription: true,
    },
    { name: "Abonamente", path: "/abonamente", icon: CreditCard },
  ];

  const sidebarLinks = allSidebarLinks.filter(
    (link) => !link.feature || features[link.feature]
  );

  const progress =
    questions.length > 0 ? ((currentIndex + 1) / questions.length) * 100 : 0;

  return (
    <div className="min-h-screen bg-slate-100" data-testid="tests-page">
      <div className="lg:hidden bg-white border-b sticky top-0 z-40">
        <div className="flex items-center justify-between p-4">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-10 h-10 bg-blue-600 rounded-lg flex items-center justify-center">
              <Car className="w-6 h-6 text-white" />
            </div>
            <span className="font-bold text-lg font-['Archivo']">AUTO CORA</span>
          </Link>
          <button onClick={() => setSidebarOpen(!sidebarOpen)} className="p-2">
            {sidebarOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      <div className="flex">
        <aside
          className={`
            fixed lg:sticky top-0 left-0 h-screen w-64 bg-slate-900 text-white z-50
            transform transition-transform duration-300 lg:transform-none
            ${sidebarOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"}
          `}
        >
          <div className="p-6 border-b border-slate-700">
            <Link to="/" className="flex items-center gap-2">
              <div className="relative">
                <div className="w-10 h-10 bg-blue-600 rounded-lg flex items-center justify-center">
                  <Car className="w-6 h-6 text-white" />
                </div>
                <div className="absolute -top-1 -right-1 w-3 h-3 bg-yellow-400 rounded-full" />
              </div>
              <span className="font-bold text-lg font-['Archivo']">AUTO CORA</span>
            </Link>
          </div>

          <nav className="p-4 space-y-2">
            {sidebarLinks.map((link) => {
              const isDisabled = link.requiresSubscription && !isSubscriptionActive;

              return (
                <Link
                  key={link.path}
                  to={isDisabled ? "/abonamente" : link.path}
                  onClick={() => setSidebarOpen(false)}
                  className={`
                    flex items-center gap-3 px-4 py-3 rounded-lg transition-colors
                    ${
                      window.location.pathname === link.path
                        ? "bg-blue-600 text-white"
                        : "text-slate-300 hover:bg-slate-800"
                    }
                    ${isDisabled ? "opacity-50" : ""}
                  `}
                >
                  <link.icon className="w-5 h-5" />
                  <span>{link.name}</span>
                  {isDisabled && <Crown className="w-4 h-4 ml-auto text-yellow-400" />}
                </Link>
              );
            })}
          </nav>

          <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-slate-700">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-slate-700 rounded-full flex items-center justify-center">
                <User className="w-5 h-5" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-medium truncate">{user?.name}</p>
                <p className="text-sm text-slate-400 truncate">{user?.email}</p>
              </div>
            </div>
            <Button
              onClick={handleLogout}
              variant="outline"
              className="w-full border-slate-600 text-slate-300 hover:bg-slate-800 bg-transparent"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Deconectare
            </Button>
          </div>
        </aside>

        {sidebarOpen && (
          <div
            className="fixed inset-0 bg-black/50 z-40 lg:hidden"
            onClick={() => setSidebarOpen(false)}
          />
        )}

        <main className="flex-1 p-4 lg:p-8 min-h-screen">
          <div className="max-w-6xl mx-auto">
            {!isSubscriptionActive && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="mb-6"
              >
                <Card className="border-yellow-200 bg-yellow-50">
                  <CardContent className="p-4 flex items-center justify-between flex-wrap gap-4">
                    <div className="flex items-center gap-3">
                      <AlertCircle className="w-6 h-6 text-yellow-600" />
                      <div>
                        <p className="font-medium text-yellow-800">Abonament inactiv</p>
                        <p className="text-sm text-yellow-700">
                          Activează un abonament Standard sau Premium pentru teste de
                          antrenament și capitole.
                        </p>
                      </div>
                    </div>
                    <Link to="/abonamente">
                      <Button className="bg-yellow-600 hover:bg-yellow-700 text-white">
                        Activează Acum
                      </Button>
                    </Link>
                  </CardContent>
                </Card>
              </motion.div>
            )}

            <AnimatePresence mode="wait">
              {/* VIEW: CATEGORIES */}
              {view === "categories" && (
                <motion.div
                  key="categories"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="space-y-8"
                >
                  <div className="text-center">
                    <h1 className="text-3xl lg:text-4xl font-bold text-slate-900 font-['Archivo'] mb-2">
                      Chestionare de Antrenament
                    </h1>
                    <p className="text-slate-600 text-lg">
                      Alege categoria sau exersează direct pe capitole
                    </p>
                  </div>

                  <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                    {/* Card Chestionare pe capitole - PRIMA POZIȚIE */}
                    <motion.div
                      key="chapters-card"
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0 }}
                    >
                      <Card
                        className="group cursor-pointer overflow-hidden border-0 shadow-lg hover:shadow-xl transition-all duration-300 h-full"
                        onClick={goToChapterQuizzes}
                        data-testid="category-chapters"
                      >
                        <div className="h-32 bg-gradient-to-br from-slate-700 to-slate-900 flex items-center justify-center relative overflow-hidden">
                          <div className="absolute inset-0 bg-black/10 group-hover:bg-black/0 transition-colors" />
                          <BookOpen className="w-16 h-16 text-white/90 group-hover:scale-110 transition-transform duration-300" />
                          <div className="absolute top-3 right-3">
                            <Badge className="bg-white/20 text-white border-0 text-lg font-bold px-3">
                              18
                            </Badge>
                          </div>
                        </div>

                        <CardContent className="p-5">
                          <h3 className="font-bold text-lg text-slate-900 mb-1">
                            Chestionare pe capitole
                          </h3>
                          <p className="text-sm text-slate-500 mb-3">
                            18 capitole de antrenament
                          </p>
                          <p className="text-sm text-slate-600 line-clamp-2">
                            Exersează separat pe fiecare capitol, în același stil al
                            platformei.
                          </p>

                          <Button
                            className="w-full mt-4 bg-gradient-to-r from-slate-700 to-slate-900 hover:opacity-90 text-white border-0"
                            type="button"
                          >
                            Vezi capitolele
                            <ChevronRight className="w-4 h-4 ml-2" />
                          </Button>
                        </CardContent>
                      </Card>
                    </motion.div>

                    {/* Categorii de permis */}
                    {licenseCategories.map((cat, index) => {
                      const Icon = categoryIcons[cat.code] || Car;
                      const colors = categoryColors[cat.code] || categoryColors.B;

                      return (
                        <motion.div
                          key={cat.code}
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: (index + 1) * 0.05 }}
                        >
                          <Card
                            className="group overflow-hidden border-0 shadow-lg transition-all duration-300 h-full cursor-pointer hover:shadow-xl"
                            onClick={() => selectLicenseCategory(cat)}
                            data-testid={`category-${cat.code}`}
                          >
                            <div
                              className={`h-32 bg-gradient-to-br ${colors.bg} flex items-center justify-center relative overflow-hidden`}
                            >
                              <div className="absolute inset-0 bg-black/10 group-hover:bg-black/0 transition-colors" />
                              <Icon className="w-16 h-16 text-white/90 group-hover:scale-110 transition-transform duration-300" />
                              <div className="absolute top-3 right-3">
                                <Badge className="bg-white/20 text-white border-0 text-lg font-bold px-3">
                                  {cat.code}
                                </Badge>
                              </div>
                              {!hasStandardAccess && (
                                <div className="absolute top-3 left-3">
                                  <Lock className="w-5 h-5 text-white/80" />
                                </div>
                              )}
                            </div>

                            <CardContent className="p-5">
                              <h3 className="font-bold text-lg text-slate-900 mb-1">
                                {cat.name}
                              </h3>
                              <p className="text-sm text-slate-500 mb-3">
                                {cat.subcategories}
                              </p>
                              <p className="text-sm text-slate-600 line-clamp-2">
                                {cat.description}
                              </p>

                              <Button
                                className={`w-full mt-4 bg-gradient-to-r ${colors.bg} text-white border-0 hover:opacity-90`}
                                type="button"
                              >
                                {hasStandardAccess ? "Intră și învață" : "Deblochează pachetul"}
                                <ChevronRight className="w-4 h-4 ml-2" />
                              </Button>
                            </CardContent>
                          </Card>
                        </motion.div>
                      );
                    })}
                  </div>
                </motion.div>
              )}

              {/* VIEW: QUIZZES LIST */}
              {view === "quizzes" && selectedCategory && (
                <motion.div
                  key="quizzes"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="space-y-6"
                >
                  <div className="flex items-center gap-4 mb-6">
                    <button
                      onClick={goBackToCategories}
                      className="flex items-center gap-2 text-slate-600 hover:text-slate-900"
                    >
                      <ArrowLeft className="w-5 h-5" />
                      <span>Înapoi la categorii</span>
                    </button>
                  </div>

                  <div className="text-center mb-8">
                    <Badge
                      className={`${categoryColors[selectedCategory.code]?.light} text-lg px-4 py-1 mb-3`}
                    >
                      {selectedCategory.code}
                    </Badge>
                    <h1 className="text-2xl lg:text-3xl font-bold text-slate-900 font-['Archivo']">
                      {selectedCategory.name}
                    </h1>
                    <p className="text-slate-600 mt-1">{selectedCategory.description}</p>
                  </div>

                  <Card className="border-0 shadow-sm">
                    <CardHeader>
                      <CardTitle className="font-['Archivo']">
                        Alege Chestionarul (1-{totalQuizzes})
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="grid grid-cols-5 sm:grid-cols-6 md:grid-cols-10 gap-2">
                        {quizzes.map((quiz) => {
                          const isAvailable = quiz.is_available;
                          const isCompleted = quiz.is_completed;
                          const isPassed = quiz.best_score >= 80;

                          return (
                            <button
                              key={quiz.number}
                              type="button"
                              onClick={(e) => {
                                e.preventDefault();
                                if (isAvailable) startQuiz(quiz.number);
                              }}
                              disabled={!isAvailable}
                              className={`
                                aspect-square rounded-lg font-bold text-lg transition-all relative
                                ${
                                  !isAvailable
                                    ? "bg-slate-100 text-slate-400 cursor-not-allowed"
                                    : isCompleted
                                    ? isPassed
                                      ? "bg-green-500 text-white hover:bg-green-600"
                                      : "bg-yellow-500 text-white hover:bg-yellow-600"
                                    : "bg-blue-100 text-blue-700 hover:bg-blue-200"
                                }
                              `}
                            >
                              {quiz.number}
                              {!isAvailable && (
                                <Lock className="w-3 h-3 absolute top-1 right-1 text-slate-400" />
                              )}
                              {isCompleted && isPassed && (
                                <CheckCircle2 className="w-3 h-3 absolute top-1 right-1" />
                              )}
                            </button>
                          );
                        })}
                      </div>

                      <div className="flex flex-wrap gap-4 mt-4 text-sm">
                        <span className="flex items-center gap-2">
                          <div className="w-4 h-4 bg-green-500 rounded" /> Promovat
                        </span>
                        <span className="flex items-center gap-2">
                          <div className="w-4 h-4 bg-yellow-500 rounded" /> Nepromovat
                        </span>
                        <span className="flex items-center gap-2">
                          <div className="w-4 h-4 bg-blue-100 rounded" /> Disponibil
                        </span>
                        <span className="flex items-center gap-2">
                          <div className="w-4 h-4 bg-slate-100 rounded" /> Indisponibil
                        </span>
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              )}

              {/* VIEW: TEST - O ÎNTREBARE PE PAGINĂ - STIL DRPCIV */}
              {view === "test" && currentQuestion && (
                <motion.div
                  key="test"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                >
                  <div className="mb-4">
                    <button
                      onClick={goBackToQuizzes}
                      className="flex items-center gap-2 text-slate-600"
                    >
                      <ArrowLeft className="w-4 h-4" />
                      Înapoi la chestionare
                    </button>
                  </div>

                  {/* Stats bar */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
                    <div className="bg-white rounded-xl shadow-sm p-4 text-center">
                      <p className="text-3xl font-bold text-slate-700">{stats.resolved}</p>
                      <p className="text-sm uppercase tracking-wide text-slate-500">Rezolvate</p>
                    </div>
                    <div className="bg-white rounded-xl shadow-sm p-4 text-center">
                      <p className="text-3xl font-bold text-slate-700">{stats.remaining}</p>
                      <p className="text-sm uppercase tracking-wide text-slate-500">Rămase</p>
                    </div>
                    <div className="bg-white rounded-xl shadow-sm p-4 text-center">
                      <p className="text-3xl font-bold text-blue-600">{currentIndex + 1}</p>
                      <p className="text-sm uppercase tracking-wide text-slate-500">Curentă</p>
                    </div>
                    <div className="bg-white rounded-xl shadow-sm p-4 text-center">
                      <p className="text-3xl font-bold text-slate-700">{totalQuestions}</p>
                      <p className="text-sm uppercase tracking-wide text-slate-500">Total</p>
                    </div>
                  </div>

                  {/* Question card */}
                  <div className="bg-white rounded-2xl shadow overflow-hidden">
                    <div className={`bg-gradient-to-r ${categoryColors[selectedCategory?.code]?.bg || 'from-blue-600 to-blue-700'} text-white p-6`}>
                      <div className="flex items-center justify-between mb-2">
                        <h1 className="text-2xl md:text-3xl font-bold uppercase">
                          Chestionar {currentQuizNumber}
                        </h1>
                        <Badge className="bg-white/20 text-white border-0">
                          {selectedCategory?.code}
                        </Badge>
                      </div>
                      <p className="text-blue-100">
                        Întrebarea {currentIndex + 1} din {totalQuestions}
                      </p>
                      <Progress value={progress} className="h-2 mt-3 bg-white/20" />
                    </div>

                    <div className="p-6 md:p-8">
                      <h2 className="text-2xl md:text-4xl text-slate-800 leading-snug mb-8">
                        {currentQuestion.text}
                      </h2>

                      <div
                        className={`grid gap-4 items-start ${
                          currentQuestion.image_url ? "lg:grid-cols-[1.2fr_0.8fr]" : "grid-cols-1"
                        }`}
                      >
                        <div className="space-y-4 order-2 lg:order-1">
                          {currentQuestion.options
                            .filter((opt) => opt && opt.text && String(opt.text).trim() !== "")
                            .map((opt, i) => {
                              const selected = isMultiple
                                ? Array.isArray(currentAnswer) && currentAnswer.includes(i)
                                : currentAnswer === i;

                              return (
                                <div
                                  key={opt.id || i}
                                  onClick={() => handleSelect(i)}
                                  className={`flex items-stretch rounded-lg border cursor-pointer overflow-hidden transition-all ${
                                    selected
                                      ? "border-green-500 shadow-md"
                                      : "border-slate-300 hover:border-blue-400"
                                  }`}
                                >
                                  <div
                                    className={`w-20 md:w-28 flex items-center justify-center text-3xl md:text-5xl font-semibold ${
                                      selected
                                        ? "bg-green-100 text-green-700"
                                        : "bg-slate-100 text-slate-600"
                                    }`}
                                  >
                                    {INDEX_TO_LETTER[i]}
                                  </div>

                                  <div className="flex-1 p-4 md:p-6 text-lg md:text-2xl text-slate-800 flex items-center">
                                    {opt.text}
                                  </div>
                                </div>
                              );
                            })}
                        </div>

                        {currentQuestion.image_url && !imageError && (
                          <div className="order-1 lg:order-2 flex justify-center">
                            <img
                              key={`${currentIndex}-${currentQuestion.image_url}`}
                              src={currentQuestion.image_url}
                              alt="Imagine întrebare"
                              className="w-full max-w-[500px] rounded-lg object-contain border bg-white p-2"
                              onError={() => setImageError(true)}
                            />
                          </div>
                        )}
                      </div>

                      {isMultiple && (
                        <p className="mt-6 text-amber-600 font-medium text-lg">
                          Selectează toate răspunsurile corecte
                        </p>
                      )}

                      <div className="flex flex-wrap justify-center gap-4 mt-10">
                        <Button
                          variant="outline"
                          onClick={prev}
                          disabled={currentIndex === 0}
                          className="min-w-[180px] h-14 text-lg"
                        >
                          Înapoi
                        </Button>

                        {currentIndex === totalQuestions - 1 ? (
                          <Button
                            onClick={submitTest}
                            disabled={!hasSelection || submitting}
                            className="min-w-[220px] h-14 text-lg bg-green-600 hover:bg-green-700"
                          >
                            {submitting ? "Se trimite..." : "Răspunde"}
                          </Button>
                        ) : (
                          <Button
                            onClick={next}
                            disabled={!hasSelection}
                            className="min-w-[220px] h-14 text-lg bg-blue-600 hover:bg-blue-700"
                          >
                            Următoarea
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                </motion.div>
              )}

              {/* VIEW: RESULTS */}
              {view === "results" && testResult && (
                <motion.div
                  key="results"
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0 }}
                >
                  {(() => {
                    // Convertim answers object în array și filtrăm greșitele
                    const answersArray = Object.values(testResult.answers || {});
                    const wrongAnswers = answersArray.filter(a => !a.is_correct);
                    const correctAnswers = answersArray.filter(a => a.is_correct);
                    
                    return (
                      <div className="max-w-4xl mx-auto">
                        {/* Header cu rezultat */}
                        <div className={`rounded-2xl shadow-xl p-8 mb-6 text-center ${
                          testResult.passed 
                            ? 'bg-gradient-to-r from-green-500 to-emerald-600' 
                            : 'bg-gradient-to-r from-red-500 to-rose-600'
                        } text-white`}>
                          <div className="w-20 h-20 mx-auto mb-4 bg-white/20 rounded-full flex items-center justify-center">
                            {testResult.passed ? (
                              <Trophy className="w-10 h-10" />
                            ) : (
                              <XCircle className="w-10 h-10" />
                            )}
                          </div>
                          <h1 className="text-4xl font-bold mb-2 font-['Archivo']">
                            {testResult.passed ? "ADMIS" : "RESPINS"}
                          </h1>
                          <div className="flex justify-center gap-2 mb-4">
                            <Badge className="bg-white/20 text-white border-0">
                              {selectedCategory?.name}
                            </Badge>
                            <Badge className="bg-white/20 text-white border-0">
                              Chestionar {currentQuizNumber}
                            </Badge>
                          </div>
                          <p className="text-5xl font-black mb-2">{testResult.percentage}%</p>
                          <p className="text-xl opacity-90">
                            {testResult.score} corecte din {testResult.total_questions} întrebări
                          </p>
                          <p className="text-sm opacity-75 mt-2">
                            Minim necesar: 22 corecte (80%)
                          </p>
                        </div>

                        {/* Statistici rapide */}
                        <div className="grid grid-cols-2 gap-4 mb-6">
                          <div className="bg-green-50 border border-green-200 rounded-xl p-4 text-center">
                            <CheckCircle className="w-8 h-8 text-green-600 mx-auto mb-2" />
                            <p className="text-2xl font-bold text-green-700">{correctAnswers.length}</p>
                            <p className="text-sm text-green-600">Răspunsuri corecte</p>
                          </div>
                          <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-center">
                            <XCircle className="w-8 h-8 text-red-600 mx-auto mb-2" />
                            <p className="text-2xl font-bold text-red-700">{wrongAnswers.length}</p>
                            <p className="text-sm text-red-600">Răspunsuri greșite</p>
                          </div>
                        </div>

                        {/* Întrebări greșite - detaliate */}
                        {wrongAnswers.length > 0 && (
                          <div className="mb-6">
                            <h2 className="text-xl font-bold text-slate-800 mb-4 flex items-center gap-2">
                              <XCircle className="w-5 h-5 text-red-500" />
                              Întrebări greșite ({wrongAnswers.length})
                            </h2>
                            <div className="space-y-4">
                              {wrongAnswers.map((item, idx) => (
                                <WrongAnswerCard key={idx} item={item} index={idx} />
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Butoane de acțiune */}
                        <div className="flex flex-col sm:flex-row gap-3 justify-center">
                          <Button
                            onClick={() => startQuiz(currentQuizNumber)}
                            className="bg-blue-600 hover:bg-blue-700"
                          >
                            <RotateCcw className="w-4 h-4 mr-2" />
                            Încearcă din nou
                          </Button>
                          <Button
                            variant="outline"
                            onClick={goBackToQuizzes}
                          >
                            <ArrowLeft className="w-4 h-4 mr-2" />
                            Înapoi la chestionare
                          </Button>
                        </div>
                      </div>
                    );
                  })()}
                </motion.div>
              )}
            </AnimatePresence>

            {loading && (view === "categories" || view === "quizzes") && (
              <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50">
                <div className="bg-white p-6 rounded-xl shadow-xl flex items-center gap-4">
                  <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
                  <span className="font-medium">Se încarcă...</span>
                </div>
              </div>
            )}
          </div>
        </main>
      </div>
    </div>
  );
}
